import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "../../../shared/contexts/LanguageContext";
import { useStepRedirect, skipNextStepRedirect } from "../../../hooks/useStepRedirect";
import { useProgressWithAutoRefresh } from "../../../shared/contexts/ProgressContext";
import ResponsiveFormLayout from "../../../shared/components/layout/ResponsiveFormLayout";
import CooperationLoader from "../../../shared/components/feedback/CooperationLoader";
import { ClosureApiService } from "../../closure/api/ClosureApiService";
import { normalizeAttachments } from "../../../shared/utils/attachmentUtils";
import { FaUpload, FaCheckCircle } from "react-icons/fa";
import WebcamCapture from "../../../shared/components/ui/WebcamCapture";
import ClinicRoomsUpload, { RoomDocumentState } from "../../../shared/components/ui/ClinicRoomsUpload";
import { hasPendingFiles, useUnsavedChanges } from "../../../shared/utils/unsavedChanges";
import { ACCEPT_IMAGES, checkUploadFile, uploadProblemKey } from "../../../shared/utils/uploadPolicy";

interface DocumentState {
  file: File | null;
  preview: string | null;
  uploaded: boolean;
  uploading: boolean;
  existingUrl?: string;
}

const ClosureDocumentsScreen: React.FC = () => {
  useStepRedirect();

  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { progress, isLoading: progressLoading } = useProgressWithAutoRefresh();
  const closureApi = useMemo(() => new ClosureApiService(), []);

  // Get closure request from progress API (primary source)
  const closureRequest = progress?.active_requests?.find(
    (r: any) => r.request_type === 'closure'
  );

  // Get state from navigation as fallback
  const locationState = (location.state as {
    requestId?: string;
    clinicName?: string;
    fromReview?: boolean;
  }) || {};

  const fromReview = !!locationState.fromReview;
  const isRegisteredClosure = !!progress?.clinic_id;

  // Use progress API data first, fallback to navigation state
  const requestId = closureRequest?.request_id || locationState.requestId;
  const clinicName = locationState.clinicName;

  // Document states
  const [syndicateOrder, setSyndicateOrder] = useState<DocumentState>({
    file: null,
    preview: null,
    uploaded: false,
    uploading: false,
  });

  const [signboardRemovalOuter, setSignboardRemovalOuter] = useState<DocumentState>({
    file: null,
    preview: null,
    uploaded: false,
    uploading: false,
  });

  const [signboardRemovalInner, setSignboardRemovalInner] = useState<DocumentState>({
    file: null,
    preview: null,
    uploaded: false,
    uploading: false,
  });

  const [clinicRooms, setClinicRooms] = useState<RoomDocumentState[]>([
    { file: null, preview: null, uploaded: false, uploading: false }
  ]);
  const [otherAttachments, setOtherAttachments] = useState<RoomDocumentState[]>([]);
  // Files picked on this device but not uploaded yet: ask before a design switch discards them.
  useUnsavedChanges(hasPendingFiles(syndicateOrder, signboardRemovalOuter, signboardRemovalInner, clinicRooms, otherAttachments));

  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedData = useRef(false);

  // Webcam state
  const [webcamOpen, setWebcamOpen] = useState(false);
  const [currentDocType, setCurrentDocType] = useState<'outer' | 'inner' | 'syndicate' | null>(null);

  // Load existing documents when component mounts
  useEffect(() => {
    if (hasLoadedData.current || !requestId) return;
    hasLoadedData.current = true;

    const loadExistingDocuments = async () => {
      setDataLoading(true);
      try {
        const detailResult = await closureApi.getClosureWithAttachments(requestId);
        if (detailResult.isSuccess && detailResult.data) {
          const documents = normalizeAttachments(
            detailResult.data.attachments || detailResult.data.documents
          );

          if (documents.syndicate_order) {
            setSyndicateOrder({
              file: null,
              preview: documents.syndicate_order,
              uploaded: true,
              uploading: false,
              existingUrl: documents.syndicate_order,
            });
          }

          if (documents.clinic_signboard_removal_outer) {
            setSignboardRemovalOuter({
              file: null,
              preview: documents.clinic_signboard_removal_outer,
              uploaded: true,
              uploading: false,
              existingUrl: documents.clinic_signboard_removal_outer,
            });
          }

          if (documents.clinic_signboard_removal_inner) {
            setSignboardRemovalInner({
              file: null,
              preview: documents.clinic_signboard_removal_inner,
              uploaded: true,
              uploading: false,
              existingUrl: documents.clinic_signboard_removal_inner,
            });
          }

          if (documents.clinic_rooms && Array.isArray(documents.clinic_rooms)) {
            const roomStates = documents.clinic_rooms.map((url: string) => ({
              file: null,
              preview: url,
              uploaded: true,
              uploading: false,
              existingUrl: url,
            }));
            setClinicRooms(roomStates);
          }

          // Load other attachment slots — prefer per-slot types, fall back to legacy array.
          const legacyOther = documents.other_attachments
            ? (Array.isArray(documents.other_attachments) ? documents.other_attachments : [documents.other_attachments])
            : [];
          const slot1Url = documents.clinic_other_attachment_1 || legacyOther[0];
          const slot2Url = documents.clinic_other_attachment_2 || legacyOther[1];
          const otherSlots = [slot1Url, slot2Url]
            .filter(Boolean)
            .map((url: string) => ({
              file: null,
              preview: url,
              uploaded: true,
              uploading: false,
              existingUrl: url,
            }));
          if (otherSlots.length > 0) {
            setOtherAttachments(otherSlots);
          }
        }
      } catch (err) {
        console.error('[ClosureDocumentsScreen] Failed to load existing documents:', err);
      } finally {
        setDataLoading(false);
      }
    };

    loadExistingDocuments();
  }, [requestId, closureApi]);

  // Show loading while waiting for progress data
  if (progressLoading || dataLoading) {
    return (
      <div className="fixed top-0 left-0 right-0 bottom-0 bg-white flex justify-center items-center">
        <CooperationLoader />
      </div>
    );
  }

  // Redirect if no request ID
  if (!requestId) {
    navigate("/home", { replace: true });
    return null;
  }

  const handleFileSelect = (
    type: 'outer' | 'inner' | 'syndicate',
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Same type/size rules as the server (shared/utils/uploadPolicy).
    const problem = checkUploadFile(file, { allowPdf: false });
    if (problem) {
      setError(t(uploadProblemKey(problem, false)));
      return;
    }

    const previewUrl = URL.createObjectURL(file);

    if (type === 'outer') {
      setSignboardRemovalOuter({ file, preview: previewUrl, uploaded: false, uploading: false });
    } else if (type === 'inner') {
      setSignboardRemovalInner({ file, preview: previewUrl, uploaded: false, uploading: false });
    } else if (type === 'syndicate') {
      setSyndicateOrder({ file, preview: previewUrl, uploaded: false, uploading: false });
    }
  };

  const handleWebcamCapture = (file: File) => {
    if (!currentDocType) return;

    const previewUrl = URL.createObjectURL(file);

    if (currentDocType === 'outer') {
      setSignboardRemovalOuter({ file, preview: previewUrl, uploaded: false, uploading: false });
    } else if (currentDocType === 'inner') {
      setSignboardRemovalInner({ file, preview: previewUrl, uploaded: false, uploading: false });
    } else if (currentDocType === 'syndicate') {
      setSyndicateOrder({ file, preview: previewUrl, uploaded: false, uploading: false });
    }
  };

  const openWebcam = (type: 'outer' | 'inner' | 'syndicate') => {
    setCurrentDocType(type);
    setWebcamOpen(true);
  };

  const uploadDocument = async (file: File, documentType: string): Promise<boolean> => {
    if (!requestId) {
      console.error('No request ID available for upload');
      setError(t('registration_request_not_found') || 'Request not found');
      return false;
    }

    try {
      const result = await closureApi.uploadAttachment(requestId, file, documentType);

      if (result.isSuccess) {
        console.log(`Uploaded ${documentType} successfully`);
        return true;
      } else {
        console.error(`Failed to upload ${documentType}:`, result.error);
        setError(result.error || t('failed_to_upload_document_type')?.replace('{type}', documentType) || 'Upload failed');
        return false;
      }
    } catch (err) {
      console.error(`Error uploading ${documentType}:`, err);
      setError(err instanceof Error ? err.message : t('upload_failed') || 'Upload failed');
      return false;
    }
  };

  const handleSubmit = async () => {
    // Validate required documents
    if (!syndicateOrder.file && !syndicateOrder.existingUrl) {
      setError(t('please_upload_syndicate_order') || 'Please upload the syndicate opening order');
      return;
    }

    if (!signboardRemovalOuter.file && !signboardRemovalOuter.existingUrl) {
      setError(t('please_upload_signboard_removal_outer') || 'Please upload outer signboard removal photo');
      return;
    }

    if (!signboardRemovalInner.file && !signboardRemovalInner.existingUrl) {
      setError(t('please_upload_signboard_removal_inner') || 'Please upload inner signboard removal photo');
      return;
    }

    // Validate at least one clinic room photo
    const hasValidRoom = clinicRooms.some(room => room.file || room.existingUrl);
    if (!hasValidRoom) {
      setError(t('please_upload_at_least_one_room_photo') || 'Please upload at least one clinic room photo');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let allSuccess = true;

      // Upload syndicate opening order
      if (syndicateOrder.file) {
        setSyndicateOrder(prev => ({ ...prev, uploading: true }));
        const success = await uploadDocument(syndicateOrder.file, 'syndicate_order');
        setSyndicateOrder(prev => ({ ...prev, uploading: false, uploaded: success }));
        if (!success) allSuccess = false;
      }

      // Upload signboard removal outer
      if (signboardRemovalOuter.file) {
        setSignboardRemovalOuter(prev => ({ ...prev, uploading: true }));
        const success = await uploadDocument(signboardRemovalOuter.file, 'clinic_signboard_removal_outer');
        setSignboardRemovalOuter(prev => ({ ...prev, uploading: false, uploaded: success }));
        if (!success) allSuccess = false;
      }

      // Upload signboard removal inner
      if (signboardRemovalInner.file) {
        setSignboardRemovalInner(prev => ({ ...prev, uploading: true }));
        const success = await uploadDocument(signboardRemovalInner.file, 'clinic_signboard_removal_inner');
        setSignboardRemovalInner(prev => ({ ...prev, uploading: false, uploaded: success }));
        if (!success) allSuccess = false;
      }

      // Upload clinic room photos
      for (let index = 0; index < clinicRooms.length; index++) {
        const room = clinicRooms[index];
        if (room.file) {
          setClinicRooms(prev => {
            const newRooms = [...prev];
            newRooms[index] = { ...newRooms[index], uploading: true };
            return newRooms;
          });

          const success = await uploadDocument(room.file, 'clinic_rooms');

          setClinicRooms(prev => {
            const newRooms = [...prev];
            newRooms[index] = { ...newRooms[index], uploading: false, uploaded: success };
            return newRooms;
          });

          if (!success) allSuccess = false;
        }
      }

      // Upload other_attachments
      for (let index = 0; index < otherAttachments.length; index++) {
        const att = otherAttachments[index];
        if (att.file) {
          setOtherAttachments(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], uploading: true };
            return updated;
          });

          const slotType = index === 0 ? 'clinic_other_attachment_1' : 'clinic_other_attachment_2';
          const success = await uploadDocument(att.file, slotType);

          setOtherAttachments(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], uploading: false, uploaded: success };
            return updated;
          });

          if (!success) allSuccess = false;
        }
      }

      if (allSuccess) {
        console.log('All closure documents uploaded successfully');
        skipNextStepRedirect();
        navigate('/closure-review');
      } else {
        console.error('Some document(s) failed to upload');
        setError(t('some_documents_failed_to_upload') || 'Some documents failed to upload');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(err instanceof Error ? err.message : t('failed_to_upload_documents') || 'Failed to upload documents');
    } finally {
      setLoading(false);
    }
  };

  const DocumentUploadCard = ({
    title,
    description,
    state,
    onFileSelect,
    onTakePhoto,
    required = false,
    inputId,
  }: {
    title: string;
    description: string;
    state: DocumentState;
    onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onTakePhoto: () => void;
    required?: boolean;
    inputId: string;
  }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900">
            {title}
            {required && <span className="text-red-500 ml-1">*</span>}
          </h3>
          <p className="text-sm text-gray-600 mt-1">{description}</p>
        </div>
      </div>

      {state.preview ? (
        <div className="relative">
          <img
            src={state.preview}
            alt={title}
            className="w-full h-48 object-cover rounded-lg"
          />
          {state.uploaded && !state.uploading && (
            <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-2">
              <FaCheckCircle size={20} />
            </div>
          )}
          {state.uploading && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
            </div>
          )}
          <label className={`mt-3 w-full px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 ${
            state.uploading || loading
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer'
          }`}>
            <FaUpload />
            <span>{t('change_photo') || 'Change Photo'}</span>
            <input
              type="file"
              accept={ACCEPT_IMAGES}
              onChange={onFileSelect}
              className="hidden"
              disabled={state.uploading || loading}
            />
          </label>
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-3 mb-2">
            <button
              onClick={onTakePhoto}
              disabled={state.uploading || loading}
              type="button"
              className="flex-1 flex flex-col items-center justify-center h-32 border-2 border-blue-400 bg-white rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="text-4xl mb-2">📷</div>
              <span className="text-sm font-medium text-gray-700">{t('take_photo') || 'Take Photo'}</span>
            </button>

            <span className="text-sm text-gray-500 font-medium">{t('or') || 'Or'}</span>

            <label
              htmlFor={`${inputId}-file`}
              className="flex-1 flex flex-col items-center justify-center h-32 border-2 border-blue-400 bg-white rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <FaUpload className="text-3xl text-gray-600 mb-2" />
              <span className="text-sm font-medium text-gray-700">{t('choose_from_files') || 'Choose from Files'}</span>
              <input
                id={`${inputId}-file`}
                type="file"
                className="hidden"
                accept={ACCEPT_IMAGES}
                onChange={onFileSelect}
                disabled={state.uploading || loading}
              />
            </label>
          </div>

          <p className="text-xs text-center text-gray-400">{t('png_jpg_up_to_10mb') || 'PNG, JPG up to 10MB'}</p>
        </div>
      )}
    </div>
  );

  const handleBack = () => {
    skipNextStepRedirect();
    if (fromReview) {
      navigate("/closure-review");
    } else if (isRegisteredClosure) {
      navigate("/clinic-closure");
    } else {
      navigate("/existing-clinic-closure");
    }
  };

  const actionButtons = (
    <div className="flex gap-3 w-full">
      <button
        onClick={handleBack}
        disabled={loading}
        className="min-h-[56px] px-6 py-4 rounded-2xl text-base font-semibold border border-gray-300 text-gray-700 hover:bg-gray-50 transition-all duration-200"
      >
        {t('back') || 'Back'}
      </button>
      <button
        onClick={handleSubmit}
        disabled={loading}
        className={`flex-1 min-h-[56px] px-6 py-4 rounded-2xl text-white text-base font-semibold transition-all duration-200 flex items-center justify-center gap-3 ${
          loading
            ? 'opacity-50 cursor-not-allowed'
            : 'hover:opacity-90 cursor-pointer shadow-md hover:shadow-lg'
        }`}
        style={{
          backgroundColor: loading ? '#B0B0B0' : '#2E5D7D',
        }}
      >
        {loading ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
            <span>{t('uploading') || 'Uploading...'}</span>
          </>
        ) : (
          <span>{t('upload_and_continue') || 'Upload and Continue'}</span>
        )}
      </button>
    </div>
  );

  return (
    <ResponsiveFormLayout
      onBack={handleBack}
      showBackButton={true}
      showLanguageToggle={true}
      icon="📄"
      iconSize="w-24 h-24 text-6xl"
      title={t("upload_closure_documents") || "Upload Documents"}
      subtitle={clinicName || t("closure_documents_subtitle") || "Required documents for closure request"}
      actions={actionButtons}
      maxHeight="90vh"
    >
      <div className="w-full space-y-6">
        {/* Error Message */}
        {error && (
          <div className="w-full rounded-xl p-4 bg-red-50 border border-red-200">
            <div className="flex items-start gap-3">
              <span className="text-red-500 text-lg">⚠️</span>
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Info Message */}
        <div className="p-4 rounded-xl bg-blue-50 border border-blue-200">
          <div className="flex items-start gap-3">
            <span className="text-blue-500 text-2xl">ℹ️</span>
            <div>
              <h4 className="font-semibold text-blue-800 mb-1">
                {t("upload_documents_info_title") || "Upload Required Documents"}
              </h4>
              <p className="text-sm text-blue-700">
                {t("closure_photos_note") ||
                  "Please upload photos showing the removed clinic signboards and current state of the clinic rooms."}
              </p>
            </div>
          </div>
        </div>

        {/* Syndicate Opening Order (الأمر النقابي بفتح العيادة) */}
        <DocumentUploadCard
          title={t('syndicate_order') || 'Syndicate Opening Order'}
          description={t('syndicate_order_description') || 'Upload an image of the syndicate order for opening the clinic'}
          state={syndicateOrder}
          onFileSelect={(e) => handleFileSelect('syndicate', e)}
          onTakePhoto={() => openWebcam('syndicate')}
          required
          inputId="syndicate-order"
        />

        {/* Signboard Removal Outer */}
        <DocumentUploadCard
          title={t('clinic_signboard_removal_outer') || 'Signboard Removal (Outer)'}
          description={t('clinic_signboard_removal_outer_description') || 'Photo showing the outer signboard has been removed'}
          state={signboardRemovalOuter}
          onFileSelect={(e) => handleFileSelect('outer', e)}
          onTakePhoto={() => openWebcam('outer')}
          required
          inputId="signboard-removal-outer"
        />

        {/* Signboard Removal Inner */}
        <DocumentUploadCard
          title={t('clinic_signboard_removal_inner') || 'Signboard Removal (Inner)'}
          description={t('clinic_signboard_removal_inner_description') || 'Photo showing the inner signboard has been removed'}
          state={signboardRemovalInner}
          onFileSelect={(e) => handleFileSelect('inner', e)}
          onTakePhoto={() => openWebcam('inner')}
          required
          inputId="signboard-removal-inner"
        />

        {/* Clinic Rooms */}
        <ClinicRoomsUpload
          rooms={clinicRooms}
          onRoomsChange={setClinicRooms}
          onError={setError}
          loading={loading}
        />

        {/* Other Attachments (optional, max 2) */}
        <ClinicRoomsUpload
          rooms={otherAttachments}
          onRoomsChange={setOtherAttachments}
          onError={setError}
          loading={loading}
          title={t('other_attachments') || 'Other Attachments'}
          required={false}
          maxCount={2}
          itemLabel={t('attachment') || 'Attachment'}
        />
      </div>

      {/* Webcam Modal */}
      {webcamOpen && (
        <WebcamCapture
          onCapture={handleWebcamCapture}
          onClose={() => setWebcamOpen(false)}
        />
      )}
    </ResponsiveFormLayout>
  );
};

export default ClosureDocumentsScreen;
